import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash } from "@phosphor-icons/react";
import { Section, NextBar } from "./Fields";
import { CONDITIONS, CONDITION_STYLE } from "./constants";

export default function ChecklistStep({
  inspection, property,
  addChecklistItem, updateChecklistItem, removeChecklistItem,
  onPrev, onNext,
}) {
  // Auto-expand bedrooms/bathrooms based on the property record.
  const dynamicRooms = useMemo(() => {
    const beds = Math.max(1, Number(property?.bedrooms) || 1);
    const baths = Math.max(1, Number(property?.bathrooms) || 1);
    const expand = (label, count) =>
      count === 1 ? [label] : Array.from({ length: count }, (_, i) => `${label} ${i + 1}`);
    return [
      "Exterior", "Hallway", "Living Room", "Kitchen", "Dining Room",
      ...expand("Bedroom", beds),
      ...expand("Bathroom", baths),
      "Utility Room", "Storage", "Garden", "Garage",
    ];
  }, [property]);

  const [extraRooms, setExtraRooms] = useState([]);
  const [newRoom, setNewRoom] = useState("");

  // Include any room that already has checklist items (covers loaded inspections).
  const allRooms = useMemo(() => {
    const fromChecklist = (inspection.checklist || []).map((c) => c.room);
    const seen = new Set();
    const out = [];
    [...dynamicRooms, ...extraRooms, ...fromChecklist].forEach((r) => {
      if (r && !seen.has(r)) {
        seen.add(r);
        out.push(r);
      }
    });
    return out;
  }, [dynamicRooms, extraRooms, inspection.checklist]);

  const addRoom = () => {
    const name = newRoom.trim();
    if (!name) return;
    if (!allRooms.includes(name)) setExtraRooms([...extraRooms, name]);
    setNewRoom("");
    toast.success(`Room "${name}" added`);
  };

  return (
    <Section title="Inspection checklist" subtitle="Room-by-room condition assessment.">
      <div className="trent-card p-4 bg-[#FAFAF7] mb-5 flex flex-col md:flex-row gap-2 md:items-end">
        <div className="flex-1">
          <span className="text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">
            Add custom room
          </span>
          <input
            value={newRoom}
            onChange={(e) => setNewRoom(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRoom())}
            placeholder="e.g. En-suite, Master Bedroom, Toilet, Walk-in wardrobe"
            className="trent-input mt-1"
            data-testid="custom-room-input"
          />
        </div>
        <button onClick={addRoom} className="trent-btn-primary" data-testid="add-custom-room-btn">
          <Plus weight="bold" size={14} /> Add room
        </button>
      </div>

      <div className="space-y-6">
        {allRooms.map((room) => {
          const items = (inspection.checklist || []).filter((c) => c.room === room);
          return (
            <div key={room} className="border border-[#E5E7EB] rounded-md overflow-hidden">
              <div className="flex items-center justify-between bg-[#F3F4F6] px-4 py-2">
                <div className="font-semibold text-[#1E3A5F]">{room}</div>
                <button
                  onClick={() => addChecklistItem(room)}
                  data-testid={`add-checklist-${room.replace(/\s+/g, "-").toLowerCase()}`}
                  className="text-xs font-semibold uppercase tracking-wider text-[#1E3A5F] hover:text-[#16A34A] inline-flex items-center gap-1"
                >
                  <Plus size={12} /> Add item
                </button>
              </div>
              {items.length === 0 ? (
                <div className="px-4 py-3 text-xs text-[#6B7280]">No items yet.</div>
              ) : (
                <div className="divide-y divide-[#E5E7EB]">
                  {items.map((c) => (
                    <div key={c.id} className="p-3 grid md:grid-cols-12 gap-2 items-center" data-testid={`checklist-${c.id}`}>
                      <input
                        value={c.item}
                        onChange={(e) => updateChecklistItem(c.id, { item: e.target.value })}
                        placeholder="Item name"
                        className="trent-input md:col-span-3"
                      />
                      <select
                        value={c.condition}
                        onChange={(e) => updateChecklistItem(c.id, { condition: e.target.value })}
                        className="trent-input md:col-span-2"
                      >
                        {CONDITIONS.map((co) => <option key={co}>{co}</option>)}
                      </select>
                      <input
                        value={c.notes}
                        onChange={(e) => updateChecklistItem(c.id, { notes: e.target.value })}
                        placeholder="Notes"
                        className="trent-input md:col-span-4"
                      />
                      <label className="flex items-center gap-2 text-xs md:col-span-2">
                        <input type="checkbox" checked={!!c.repair_required} onChange={(e) => updateChecklistItem(c.id, { repair_required: e.target.checked })} />
                        Repair
                      </label>
                      <div className="md:col-span-1 flex items-center justify-end gap-2">
                        <span className={`condition-pill ${CONDITION_STYLE[c.condition]}`}>{c.condition[0]}</span>
                        <button onClick={() => removeChecklistItem(c.id)} className="text-[#991B1B] p-1 hover:bg-[#FEE2E2] rounded-sm">
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <NextBar onPrev={onPrev} onNext={onNext} testId="wizard-next-5" />
    </Section>
  );
}
